/**
 * SystemOptionCard Component
 *
 * Displays a single system option with:
 * - Efficiency ratings (SEER2/AFUE)
 * - Monthly payment estimate
 * - Energy savings vs current system
 * - Maintenance savings
 * - Total 10-year benefit
 * - Applicable rebates (collapsed)
 */

import { useState } from 'react';

export default function SystemOptionCard({
  option,
  savings,
  rebateGroups,
  isRecommended = false,
  rank,
  financing = { enabled: false, url: '' }
}) {
  const [showRebates, setShowRebates] = useState(false);

  // Get best rebate group (highest total)
  const bestRebateGroup = rebateGroups && rebateGroups.length > 0 ? rebateGroups[0] : null;
  const totalRebates = bestRebateGroup ? bestRebateGroup.totalAmount : 0;

  // Calculate monthly savings
  const monthlySavings = savings ? Math.round((savings.energy.annualSavings + (savings.maintenance.tenYearSavings / 10)) / 12) : 0;
  const monthlyNetCost = option.monthly_payment ? option.monthly_payment - monthlySavings : null;

  return (
    <div style={{
      background: isRecommended ? '#f0fdf4' : 'white',
      border: isRecommended ? '3px solid #10b981' : '2px solid #e2e8f0',
      borderRadius: '12px',
      padding: '25px',
      position: 'relative',
      boxShadow: isRecommended ? '0 4px 12px rgba(16, 185, 129, 0.15)' : '0 2px 8px rgba(0,0,0,0.05)'
    }}>
      {/* Recommended Badge */}
      {isRecommended && (
        <div style={{
          position: 'absolute',
          top: '-12px',
          left: '20px',
          background: '#10b981',
          color: 'white',
          padding: '6px 16px',
          borderRadius: '20px',
          fontSize: '13px',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
        }}>
          ⭐ Best Value
        </div>
      )}

      {/* Rank Badge */}
      {!isRecommended && rank && (
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          background: '#f1f5f9',
          color: '#64748b',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600'
        }}>
          Option #{rank}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: '20px', marginTop: isRecommended ? '10px' : '0' }}>
        <h4 style={{
          margin: '0 0 8px 0',
          fontSize: '20px',
          color: '#1e293b',
          fontWeight: '700'
        }}>
          {option.option_name}
        </h4>

        <div style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          fontSize: '14px'
        }}>
          {option.seer2_rating && (
            <div style={{
              padding: '4px 12px',
              background: '#eff6ff',
              color: '#1e40af',
              borderRadius: '6px',
              fontWeight: '600'
            }}>
              {option.seer2_rating} SEER2
            </div>
          )}

          {option.afue_rating && (
            <div style={{
              padding: '4px 12px',
              background: option.is_condensing ? '#fef3c7' : '#f3f4f6',
              color: option.is_condensing ? '#92400e' : '#374151',
              borderRadius: '6px',
              fontWeight: '600'
            }}>
              {option.afue_rating}% AFUE
              {option.is_condensing && ' (Condensing)'}
            </div>
          )}
        </div>
      </div>

      {/* Monthly Payment */}
      {option.monthly_payment && (
        <div style={{
          padding: '20px',
          background: isRecommended ? 'white' : '#f8fafc',
          borderRadius: '10px',
          marginBottom: '20px',
          border: '2px solid #e2e8f0'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <div>
              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
                Starting at (before savings)
              </div>
              <div style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b' }}>
                ${option.monthly_payment.toLocaleString()}
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#64748b' }}>/mo</span>
              </div>
            </div>

            {monthlySavings > 0 && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '13px', color: '#10b981', marginBottom: '4px' }}>
                  Estimated Savings
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>
                  -${monthlySavings.toLocaleString()}
                  <span style={{ fontSize: '14px' }}>/mo</span>
                </div>
              </div>
            )}
          </div>

          {monthlyNetCost !== null && (
            <div style={{
              paddingTop: '12px',
              borderTop: '2px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#64748b' }}>
                Net Monthly Cost
              </div>
              <div style={{
                fontSize: '28px',
                fontWeight: '800',
                color: monthlyNetCost <= 0 ? '#10b981' : '#1e293b'
              }}>
                {monthlyNetCost <= 0 ? (
                  <span>
                    <span style={{ fontSize: '18px', color: '#10b981' }}>PAYS FOR ITSELF + </span>
                    ${Math.abs(monthlyNetCost).toLocaleString()}/mo
                  </span>
                ) : (
                  <span>${monthlyNetCost.toLocaleString()}/mo</span>
                )}
              </div>
            </div>
          )}

          <div style={{
            marginTop: '12px',
            fontSize: '12px',
            color: '#64748b',
            fontStyle: 'italic'
          }}>
            {option.finance_months} months @ {option.interest_rate}% APR
          </div>
        </div>
      )}

      {/* Savings Breakdown */}
      {savings && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '700',
            color: '#1e293b',
            marginBottom: '12px'
          }}>
            10-Year Savings vs Current System
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '12px'
          }}>
            <div style={{
              padding: '12px',
              background: '#ecfdf5',
              borderRadius: '8px',
              border: '1px solid #d1fae5'
            }}>
              <div style={{ fontSize: '11px', color: '#065f46', marginBottom: '4px', fontWeight: '600' }}>
                Energy Savings
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#059669' }}>
                ${savings.energy.tenYearSavings.toLocaleString()}
              </div>
            </div>

            <div style={{
              padding: '12px',
              background: '#eff6ff',
              borderRadius: '8px',
              border: '1px solid #dbeafe'
            }}>
              <div style={{ fontSize: '11px', color: '#1e40af', marginBottom: '4px', fontWeight: '600' }}>
                Maintenance Savings
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#2563eb' }}>
                ${savings.maintenance.tenYearSavings.toLocaleString()}
              </div>
            </div>

            {totalRebates > 0 && (
              <div style={{
                padding: '12px',
                background: '#fef3c7',
                borderRadius: '8px',
                border: '1px solid #fde68a'
              }}>
                <div style={{ fontSize: '11px', color: '#92400e', marginBottom: '4px', fontWeight: '600' }}>
                  Rebates Available
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#d97706' }}>
                  ${totalRebates.toLocaleString()}
                </div>
              </div>
            )}
          </div>

          {/* Total Benefit */}
          <div style={{
            marginTop: '15px',
            padding: '15px',
            background: isRecommended ? '#dcfce7' : '#f0fdf4',
            borderRadius: '8px',
            border: '2px solid #86efac',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#166534' }}>
              Total 10-Year Benefit
            </div>
            <div style={{ fontSize: '28px', fontWeight: '800', color: '#15803d' }}>
              ${(savings.totalTenYearBenefit + totalRebates).toLocaleString()}
            </div>
          </div>
        </div>
      )}

      {/* Rebates Section */}
      {rebateGroups && rebateGroups.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowRebates(!showRebates)}
            style={{
              width: '100%',
              padding: '12px 16px',
              background: '#fffbeb',
              border: '2px solid #fcd34d',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: '#92400e',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#fef3c7'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#fffbeb'}
          >
            <span>
              💰 {bestRebateGroup.rebates.length} Rebate{bestRebateGroup.rebates.length > 1 ? 's' : ''} Available
              (${totalRebates.toLocaleString()})
            </span>
            <span style={{ fontSize: '18px' }}>
              {showRebates ? '▼' : '▶'}
            </span>
          </button>

          {showRebates && (
            <div style={{
              marginTop: '12px',
              padding: '15px',
              background: '#fffbeb',
              borderRadius: '8px',
              border: '1px solid #fcd34d'
            }}>
              {bestRebateGroup.rebates.map((rebate, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '10px',
                    marginBottom: idx < bestRebateGroup.rebates.length - 1 ? '8px' : '0',
                    background: 'white',
                    borderRadius: '6px',
                    border: '1px solid #fde68a'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '4px'
                  }}>
                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>
                      {rebate.name}
                    </div>
                    <div style={{ fontWeight: '700', color: '#d97706', fontSize: '16px' }}>
                      ${rebate.amount.toLocaleString()}
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    {rebate.category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </div>
                  {rebate.daysUntilExpiration && rebate.daysUntilExpiration > 0 && (
                    <div style={{
                      marginTop: '6px',
                      fontSize: '11px',
                      color: rebate.urgencyColor,
                      fontWeight: '600'
                    }}>
                      ⏰ {rebate.urgencyLabel}: Expires in {rebate.daysUntilExpiration} days
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Financing Button */}
      {financing.enabled && financing.url && (
        <div style={{ marginTop: '20px' }}>
          <a
            href={financing.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block',
              width: '100%',
              padding: '14px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              textAlign: 'center',
              textDecoration: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '700',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              border: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
            }}
          >
            💳 Apply for Financing →
          </a>
          <div style={{
            marginTop: '8px',
            fontSize: '12px',
            color: '#64748b',
            textAlign: 'center'
          }}>
            Flexible payment options available
          </div>
        </div>
      )}
    </div>
  );
}
